/**
 * GlossaryTooltip
 *
 * Rich structured tooltip for portfolio terminology.
 *
 * Desktop (pointer device): Radix Tooltip primitive.
 *   - Initial open delay: 400 ms (inherits TooltipProvider default).
 *   - Moving between glossary chips: opens INSTANTLY (≤ 300 ms after last
 *     close) because the app-level TooltipProvider has skipDelayDuration=300.
 *     This eliminates the flicker gap that HoverCard caused.
 *
 * Mobile (touch / width ≤ 1023px): tap triggers a BottomSheet.
 *   - Same content, scrollable bottom drawer.
 *
 * Content layout:
 *   [color dot] [LABEL]  (font-semibold, text-sm)
 *   divider
 *   WHAT  (overline) + definition  (text-xs, text-gray-700)
 *   TRIGGERED WHEN  (overline) + threshold  (text-xs font-medium, text-gray-900)
 *   WHAT TO DO  (overline) + action  (text-xs, text-primary-700)
 *
 * Accessible: role=tooltip on Tooltip content is implicit in the Radix primitive.
 */

import { useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PORTFOLIO_GLOSSARY } from '@/constants/portfolioGlossary';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GlossaryTooltipProps {
  /** Stable key into PORTFOLIO_GLOSSARY */
  termId: string;
  /** The trigger element */
  children: React.ReactNode;
  /** Which side to prefer for the popover (desktop only) */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment relative to trigger (desktop only) */
  align?: 'start' | 'center' | 'end';
  /** When true, the trigger wrapper occupies the full width of its container.
   *  Use this when the child element relies on `width: 100%` to render at full
   *  size (e.g. a full-width PipelineBar / RunwayBar). Default is inline-flex
   *  which shrinks to content. */
  fullWidth?: boolean;
  /** Shape of the keyboard-focus frame around a mobile (BottomSheet) trigger: 'tile' (default, rounded-xl) for stats and
   *  text labels, 'pill' (fully rounded) only for pill-shaped chip triggers. */
  frameShape?: 'pill' | 'tile';
  /** When true, on mobile (tap) the trigger is rendered bare - NO tap-to-open
   *  BottomSheet - so taps fall through to a parent's click handler (e.g. a
   *  clickable brand card). Desktop hover is unaffected. Use only inside
   *  tappable cards; keep the glossary reachable via an explicit info icon or
   *  the detail view. */
  disableOnMobile?: boolean;
}

// ── Overline helper ───────────────────────────────────────────────────────────

const Overline = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mt-2.5 mb-0.5 first:mt-0">
    {children}
  </p>
);

// ── Main component ────────────────────────────────────────────────────────────

export const GlossaryTooltip = ({
  termId,
  children,
  side = 'top',
  align = 'start',
  fullWidth = false,
  frameShape = 'tile',
  disableOnMobile = false,
}: GlossaryTooltipProps) => {
  const { t } = useTranslation('agency');
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [mobileOpen, setMobileOpen] = useState(false);

  const term = PORTFOLIO_GLOSSARY[termId];

  // If the term doesn't exist, render children as-is - never crash.
  if (!term) {
    return <>{children}</>;
  }

  // Tappable-card context: let the tap reach the parent's onClick.
  if (isMobile && disableOnMobile) {
    return <>{children}</>;
  }

  const label = t(term.labelKey, { defaultValue: term.labelFallback });
  const definition = t(term.definitionKey, { defaultValue: term.definitionFallback });
  const threshold = term.thresholdKey
    ? t(term.thresholdKey, { defaultValue: term.thresholdFallback ?? '' })
    : term.thresholdFallback;
  const action = term.actionKey
    ? t(term.actionKey, { defaultValue: term.actionFallback ?? '' })
    : term.actionFallback;

  const cardContent = (
    <div className="p-4 max-w-[288px]">
      {/* Header: color dot + label */}
      <div className="flex items-center gap-2 mb-2">
        {term.dotColor && (
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${term.dotColor}`}
            aria-hidden="true"
          />
        )}
        <span className="font-semibold text-sm text-gray-900 leading-tight">{label}</span>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Definition */}
      <div className="mt-2.5">
        <Overline>{t('glossary_overline_what', { defaultValue: 'What' })}</Overline>
        <p className="text-xs text-gray-700 leading-relaxed">{definition}</p>
      </div>

      {/* Threshold */}
      {threshold && (
        <div>
          <Overline>{t('glossary_overline_when', { defaultValue: 'Triggered when' })}</Overline>
          <p className="text-xs font-medium text-gray-900 leading-relaxed">{threshold}</p>
        </div>
      )}

      {/* Action */}
      {action && (
        <div>
          <Overline>{t('glossary_overline_action', { defaultValue: 'What to do' })}</Overline>
          <p className="text-xs text-primary-700 leading-relaxed">{action}</p>
        </div>
      )}
    </div>
  );

  // ── Mobile: tap-to-open BottomSheet ──────────────────────────────────────────
  if (isMobile) {
    // BottomSheet wraps the trigger in its own button. When fullWidth is needed
    // (e.g. PipelineBar `width: 100%`), wrap the children in a w-full block so
    // the trigger occupies the full row instead of shrinking to content.
    const trigger = fullWidth ? <div className="w-full">{children}</div> : children;
    return (
      <BottomSheet
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        trigger={trigger}
        // Hug a content-sized chip so the focus frame matches it; full-width triggers keep the full row.
        triggerClassName={fullWidth ? undefined : 'w-fit'}
        triggerFrameRadius={frameShape === 'pill' && !fullWidth ? 'full' : 'xl'}
        animation="slide"
      >
        {cardContent}
      </BottomSheet>
    );
  }

  // ── Desktop: Radix Tooltip (skipDelayDuration handled by app-level Provider) ──
  return (
    <TooltipPrimitive.Root delayDuration={400}>
      <TooltipPrimitive.Trigger asChild>
        {/* Wrap in span so any non-forwardRef child still works.
         *  fullWidth → block w-full so children using `width: 100%` (PipelineBar /
         *  RunwayBar) render at full row width. Default inline-flex shrinks. */}
        <span className={fullWidth ? 'block w-full' : 'inline-flex items-center'}>
          {children}
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          avoidCollisions
          collisionPadding={{ top: 72, right: 16, bottom: 16, left: 16 }}
          className={cn(
            'z-[9999] w-[288px] max-w-[288px] rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          )}
        >
          {cardContent}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export default GlossaryTooltip;
